import { Box, Card, CardBody, CardHeader, Divider, Heading, Image, Stack, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { DocumentType } from '@pelicargo/types';
import { buildFlightPathString, Show } from '@pelicargo/ui';
import {
  getPrettyEarliestTenderOption,
  isoCountryFallback,
  isoStateFallback,
  join,
  prettyDate,
} from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { InstructionsCard } from '../../components/InstructionCard';
import { LabeledValue } from '../../components/LabeledValue';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { TrpcOutputs } from '../../config/trpc';
import { useFlightData } from '../../hooks/useFlightData';

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
};

export const AfrBookingConfirmation = ({ quote }: Props) => {
  const { flightData } = useFlightData(quote?.flight_path);

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;

  const schema = z.object({
    documents: z
      .array(
        z.object({
          id: z.number(),
          created_at: z.coerce.date().nullish(),
          updated_at: z.coerce.date().nullish(),
          original_filename: z.string(),
          type: z.nativeEnum(DocumentType, { invalid_type_error: 'Invalid Document Type' }).nullish(),
          request_id: z.number().nullish(),
          document_id: z.number().nullish(),
        }),
      )
      .optional(),
  });
  const request = quote?.request;
  const methods = useForm<{
    documents: Array<{
      id: number;
      created_at: Date | null;
      updated_at: Date | null;
      original_filename: string;
      type: DocumentType | null;
      request_id: number | null;
      document_id: number | null;
    }>;
  }>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: {
      documents: request?.documents || [],
    },
  });

  useEffect(() => {
    methods.reset({
      ...methods.getValues(),
      documents: request?.documents || [],
    });
  }, [request?.documents, methods]);

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  const cutoffDate = useMemo(() => {
    if (!quote?.cutoff_at) return null;
    return prettyDate(quote.cutoff_at, { showTime: true, timeZone: quote?.origin_airport?.time_zone });
  }, [quote.cutoff_at, quote?.origin_airport?.time_zone]);
  const earliestTender = getPrettyEarliestTenderOption(quote?.earliest_tender);
  const dropoffNotes = quote?.dropoff_instructions;
  const address = quote?.delivery_address;
  const addressText = useMemo(() => {
    return [
      address?.recipient,
      address?.line_one,
      address?.line_two,
      join([address?.city, isoStateFallback(address?.state), address?.zip_code, isoCountryFallback(address?.country)]),
    ]
      .filter(Boolean)
      .join('\n');
  }, [address]);

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Show if={!!quote?.booking_instructions}>
          <InstructionsCard instructions={quote?.booking_instructions} />
        </Show>
        <Show if={!!quote?.cutoff_at || !!quote?.delivery_address}>
          <Card>
            <CardHeader px="4" py="3">
              <Heading variant="h5">Drop Off</Heading>
            </CardHeader>
            <CardBody px="4" py="3">
              <Stack>
                <LabeledValue label="Cut Off Date" value={cutoffDate} defaultValue="-" />
                <LabeledValue label="Earliest Tender" value={earliestTender} hideIfEmpty />
                <LabeledValue label="Address" value={addressText} defaultValue="-" />
                <LabeledValue label="Drop Off Notes" value={dropoffNotes} hideIfEmpty />
              </Stack>
            </CardBody>
          </Card>
        </Show>
        <Card borderRadius="lg" role="group">
          <CardHeader pl="4" pr="4" pt="3" pb="3">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {quote?.origin_airport_id}{' '}
                  <Box as="span" color="text.placeholder">
                    →
                  </Box>{' '}
                  {quote?.destination_airport_id}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton text={buildFlightPathString(quote?.flight_path)} marginLeft="auto" />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
            </Stack>
          </CardHeader>
          <Stack>
            <Divider orientation="horizontal" />
          </Stack>
          <CardBody>
            <Timeline data={flightData} />
          </CardBody>
        </Card>
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI quote={quote} quoteVariant={bookedQuoteVariant} showCopy={false} />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} />
      </Stack>
    </Stack>
  );
};
